import * as React from 'react';
import '../App.css';
import {HistoryStats} from '../HistoryStats';
import {WordEntry} from '../WordEntry';
import {HistoryEvent} from '../HistoryEvent';
import {CharactersScores} from './CharactersScores';
import {WordOracle} from '../WordOracle';
import {Util} from '../Util';
import {KnownCharacterTable} from './KnownCharacterTable';
import {KatakanaWordOrder} from '../KatakanaWordOrder';
import {WordOrder} from '../WordOrder';
import {Syllabary} from '../WordFetcher';
import {HiraganaWordOrder} from '../HiraganaWordOrder';
import {Answer} from './QuizBox/Answer';
import {Quiz} from './QuizBox/Quiz';
import {CharacterIntroduction} from './QuizBox/CharacterIntroduction';
import styled, {ThemeProvider} from 'styled-components';
import {HistoryLoader} from '../HistoryLoader';
import {AppMenu} from "./AppMenu";
import {Grid} from "semantic-ui-react";
import {TelemetryData} from "../models/TelemetryData";
import {Telemetry} from "../Telemetry";
import {v4 as uuidv4} from "uuid";
import {CelebrateCompletion} from "./QuizBox/CelebrateCompletion";
import {StorageModal} from "./StorageModal";
import {DebugData} from "./DebugData";

interface IProps {
}

const SHOW_DEBUG = false;
const MIN_WORD_OPTIONS = 7;
const RECENTS_TO_SKIP = 2;
const MAX_HISTORY_LENGTH = 500;

export enum Action {
    quiz,
    reveal,
    introducing,
    celebrateCompletion,
}

interface SessionIdentifierRetrieval {
    sessionIdentifier: string,
    newlyCreated: boolean,
}

export interface AppState {
    knownCharacters: string;
    currentWord: WordEntry;
    knownWords: WordEntry[];
    recentHistory: HistoryEvent[];
    action: Action,
    currentSyllabary: Syllabary
    sessionIdentifier: string,
    playSound: boolean,
    katakanaCompleted: boolean,
    hiraganaCompleted: boolean,
    showMissingLanguageModal: boolean
}

class App extends React.Component<IProps, AppState> {
    state: AppState;
    private historyStats: HistoryStats;
    private currentCharacterOrder: string[];
    private wordOrder: WordOrder;
    private firstVisit: boolean;
    private showDebug: boolean;

    private readonly KATAKANA_ORDER = [
        'イ', 'ス', 'ト', 'ラ', 'ン', 'ク', 'ア', 'レ', 'ド', 'リ', 'マ', 'ル', 'フ', 'ブ', 'プ', 'タ', 'グ', 'ロ', 'コ', 'カ', 'ウ', 'サ', 'エ', 'テ', 'ム', 'ハ', 'バ', 'パ', 'オ', 'ナ', 'ダ', 'シ', 'セ', 'ミ', 'ニ', 'メ', 'ガ', 'ジ', 'チ', 'キ', 'モ', 'デ', 'ズ', 'ネ', 'ソ', 'ノ', 'ワ', 'ゴ', 'ヘ', 'ベ', 'ペ', 'ホ', 'ボ', 'ポ', 'ケ', 'ツ', 'ザ', 'ゲ', 'ヲ', 'ヒ', 'ビ', 'ピ', 'ヤ', 'ギ', 'ゼ', 'ヅ', 'ユ', 'ヂ', 'ゾ', 'ヌ', 'ー', 'ッ', 'ャ', 'ュ', 'ョ'
    ];
    private readonly HIRAGANA_ORDER = [
        'い', 'き', 'か', 'ん', 'て', 'た', 'さ', 'せ', 'く', 'つ', 'け', 'し', 'だ', 'は', 'な', 'ら', 'じ', 'る', 'め', 'ま', 'あ', 'が', 'す', 'う', 'み', 'わ', 'れ', 'お', 'り', 'と', 'こ', 'え', 'に', 'も', 'ど', 'ち', 'よ', 'や', 'ひ', 'む', 'の', 'そ', 'ば', 'ろ', 'ふ', 'ぶ', 'ね', 'ご', 'げ', 'で', 'ず', 'ほ', 'び', 'ざ', 'ぎ', 'ゆ', 'ぐ', 'ぞ', 'ぜ', 'へ', 'べ', 'づ', 'ぬ', 'ぢ', 'ぱ', 'ぷ', 'ぺ', 'ぽ', 'ぴ', 'ょ', 'っ', 'ゅ', 'ゃ'
    ];

    constructor(props: string) {
        super(props);
        this.historyStats = new HistoryStats();

        let currentSyllabary: Syllabary = localStorage.getItem('syllabary') == Syllabary.hiragana ? Syllabary.hiragana : Syllabary.katakana;
        let knownCharacters = this.initKnownCharacters(currentSyllabary);
        let recentHistory = HistoryLoader.initRecentHistory(currentSyllabary);
        let knownWords = this.initKnownWords(currentSyllabary, knownCharacters);
        let playSound = this.initPlaySound();
        let identifierRetrieval = this.initSessionIdentifier();
        let sessionIdentifier = identifierRetrieval.sessionIdentifier;
        let hiraganaCompleted = this.initHiraganaCompleted();
        let katakanaCompleted = this.initKatakanaCompleted();

        this.firstVisit = identifierRetrieval.newlyCreated;

        this.reveal = this.reveal.bind(this);
        this.record = this.record.bind(this);
        this.showQuiz = this.showQuiz.bind(this);
        this.changeSyllabary = this.changeSyllabary.bind(this);
        this.resetStateFromStorage = this.resetStateFromStorage.bind(this);
        this.togglePlaySound = this.togglePlaySound.bind(this);
        this.setShowMissingLanguageModal = this.setShowMissingLanguageModal.bind(this);
        this.showDebug = SHOW_DEBUG || new URLSearchParams(window.location.search).get("debug") != null

        this.state = {
            knownCharacters: knownCharacters,
            knownWords: knownWords,
            currentWord: App.getNextWord(recentHistory, knownWords, knownCharacters),
            recentHistory: recentHistory,
            action: Action.quiz,
            currentSyllabary: currentSyllabary,
            sessionIdentifier: sessionIdentifier,
            playSound: playSound,
            hiraganaCompleted: hiraganaCompleted,
            katakanaCompleted: katakanaCompleted,
            showMissingLanguageModal: false,
        };
    }

    public render() {
        return (
            <ThemeProvider theme={theme}>
                <div className='App'>
                    <AppMenu
                        currentSyllabary={this.state.currentSyllabary}
                        changeSyllabary={this.changeSyllabary}
                        resetStateFromStorage={this.resetStateFromStorage}
                        togglePlaySound={this.togglePlaySound}
                        playSound={this.state.playSound}
                        showAbout={this.firstVisit}
                        showMissingLanguageModal={this.state.showMissingLanguageModal}
                        setShowMissingLanguageModal={this.setShowMissingLanguageModal}
                    />
                    <StyledContent>
                        <Grid stackable reversed='mobile'>
                            <Grid.Column width={4}>
                                <KnownCharacterTable knownCharacters={this.state.knownCharacters}
                                                     currentWord={this.state.currentWord.reading}
                                                     currentSyllabary={this.state.currentSyllabary}/>
                            </Grid.Column>
                            <Grid.Column width={12}>
                                <Answer action={this.state.action}
                                        word={this.state.currentWord}
                                        showLanguageError={() => {
                                            this.setShowMissingLanguageModal(true)
                                        }}
                                        record={this.record}/>
                                <Quiz action={this.state.action} word={this.state.currentWord}
                                      reveal={this.reveal}/>
                                <CharacterIntroduction action={this.state.action}
                                                       knownCharacters={this.state.knownCharacters}
                                                       showQuiz={this.showQuiz}
                                                       showLanguageError={() => {
                                                           this.setShowMissingLanguageModal(true)
                                                       }}/>
                                <CelebrateCompletion action={this.state.action}
                                                     showQuiz={this.showQuiz}
                                                     currentSyllabary={this.state.currentSyllabary}
                                                     hiraganaCompleted={this.state.hiraganaCompleted}
                                                     katakanaCompleted={this.state.katakanaCompleted}/>
                            </Grid.Column>
                        </Grid>
                        <DebugData
                            showDebug={this.showDebug}
                            knownCharacters={this.state.knownCharacters}
                            history={this.state.recentHistory}
                        />
                        <CharactersScores
                            knownCharacters={this.state.knownCharacters}
                            history={this.state.recentHistory}
                            characterOrder={this.currentCharacterOrder}
                            showDebug={this.showDebug}
                        />
                        <StorageModal/>
                    </StyledContent>
                </div>
            </ThemeProvider>
        );
    }

    private initKnownWords(currentSyllabary: Syllabary, knownCharacters: string) {
        if (currentSyllabary == Syllabary.hiragana) {
            this.wordOrder = new HiraganaWordOrder();
        } else {
            this.wordOrder = new KatakanaWordOrder();
        }
        let knownWords: WordEntry[] = this.getKnownWords(knownCharacters);
        knownWords = knownWords.concat(this.wordOrder.get(''));
        return knownWords;
    }

    private initKnownCharacters(currentSyllabary: Syllabary) {
        let knownCharacters: string | null;
        if (currentSyllabary == Syllabary.katakana) {
            this.currentCharacterOrder = this.KATAKANA_ORDER;
            knownCharacters = localStorage.getItem('knownKatakanaCharacters');
        } else {
            this.currentCharacterOrder = this.HIRAGANA_ORDER;
            knownCharacters = localStorage.getItem('knownHiraganaCharacters');
        }
        if (knownCharacters == null) {
            knownCharacters = this.currentCharacterOrder.slice(0, 5).join('');
        }
        return knownCharacters;
    }

    private resetStateFromStorage(syllabary: Syllabary) {
        if (this.state.currentSyllabary === syllabary) {
            let knownCharacters = this.initKnownCharacters(syllabary);
            let recentHistory = HistoryLoader.initRecentHistory(syllabary);
            let knownWords = this.initKnownWords(syllabary, knownCharacters);

            this.reveal = this.reveal.bind(this);
            this.record = this.record.bind(this);
            this.showQuiz = this.showQuiz.bind(this);
            this.changeSyllabary = this.changeSyllabary.bind(this);

            this.setState({
                knownCharacters: knownCharacters,
                knownWords: knownWords,
                currentWord: App.getNextWord(recentHistory, knownWords, knownCharacters),
                recentHistory: recentHistory,
            });
        }
    }


    private initPlaySound(): boolean {
        return this.initBoolean('playSound', true);
    }

    private initSessionIdentifier(): SessionIdentifierRetrieval {
        let fromStorage = localStorage.getItem('sessionIdentifier');
        if (fromStorage == undefined) {
            let newSessionIdentifier = uuidv4();
            localStorage.setItem('sessionIdentifier', newSessionIdentifier);
            return {
                sessionIdentifier: newSessionIdentifier,
                newlyCreated: true,
            }
        } else {
            return {
                sessionIdentifier: fromStorage,
                newlyCreated: false,
            }
        }
    }

    private initHiraganaCompleted(): boolean {
        return this.initBoolean(Syllabary.hiragana + 'Completed', false);
    }

    private initKatakanaCompleted(): boolean {
        return this.initBoolean(Syllabary.katakana + 'Completed', false);
    }

    private initBoolean(storageName: string, startValue: boolean): boolean {
        let fromStorage = localStorage.getItem(storageName);
        let newValue
        if (fromStorage != undefined && fromStorage != '') {
            newValue = Boolean(JSON.parse(fromStorage));
        } else {
            newValue = startValue;
        }

        return newValue;
    }

    private getKnownWords(knownCharacters: string) {
        return this.wordOrder.get('').concat(knownCharacters.split('')
            .map(char => this.wordOrder.get(char))
            .reduce((agg, entries) => agg.concat(entries), []));
    }

    private changeSyllabary(newSyllabary: Syllabary) {
        localStorage.setItem('syllabary', newSyllabary.toString());

        let knownCharacters = this.initKnownCharacters(newSyllabary);
        let recentHistory = HistoryLoader.initRecentHistory(newSyllabary);
        let knownWords = this.initKnownWords(newSyllabary, knownCharacters);
        let nextWord = App.getNextWord(recentHistory, knownWords, knownCharacters);

        this.setState({
            currentSyllabary: newSyllabary,
            knownCharacters: knownCharacters,
            knownWords: knownWords,
            recentHistory: recentHistory,
            currentWord: nextWord,
            action: Action.quiz,
        })
    }

    private togglePlaySound() {
        let newValue: boolean = !this.state.playSound;
        this.setState({
            playSound: newValue,
        })
        localStorage.setItem('playSound', String(newValue));
    }

    private setShowMissingLanguageModal(newValue: boolean) {
        this.setState({
            showMissingLanguageModal: newValue
        })
    }

    private reveal() {
        Util.playSound(this.state.currentWord.reading, () => {
            this.setShowMissingLanguageModal(true)
        }, this.state.playSound);
        this.setState({action: Action.reveal});
    }

    private showQuiz() {
        this.setState({action: Action.quiz});
    }

    private record(correct: boolean) {
        let currentReading = this.state.currentWord.reading;
        let knownCharacters = this.state.knownCharacters;
        const history = this.state.recentHistory;

        const historyEvent = new HistoryEvent(currentReading, correct, history.length);
        let newKnownWords: WordEntry[] = this.state.knownWords;
        let newAction: Action = Action.quiz;
        let newHistory = history.concat([historyEvent]);
        newHistory.slice(newHistory.length - MAX_HISTORY_LENGTH, newHistory.length)
        let completed
        if (this.state.currentSyllabary == Syllabary.katakana) {
            completed = this.state.katakanaCompleted
        } else {
            completed = this.state.hiraganaCompleted
        }
        if (this.historyStats.allCorrectNTimes(knownCharacters, 5, newHistory)) {
            let nextCharacter = this.getNextCharacter(knownCharacters);
            if (nextCharacter != undefined) {
                knownCharacters += nextCharacter;
                newKnownWords = newKnownWords.concat(this.wordOrder.get(nextCharacter));
                newAction = Action.introducing;
            } else {
                if (!completed) {
                    newAction = Action.celebrateCompletion;
                }
                completed = true;
                localStorage.setItem(this.state.currentSyllabary + 'completed', 'true');
            }
        }

        const nextWord = App.getNextWord(newHistory, newKnownWords, knownCharacters);
        let newState = {
            knownCharacters: knownCharacters,
            recentHistory: newHistory,
            currentWord: nextWord,
            action: newAction,
            knownWords: newKnownWords,
            sessionIdentifier: this.state.sessionIdentifier
        };

        if (this.state.currentSyllabary == Syllabary.katakana) {
            newState['katakanaCompleted'] = completed
        } else {
            newState['hiraganaCompleted'] = completed
        }

        localStorage.setItem('known' + this.currentSyllabaryName() + 'Characters', knownCharacters);
        localStorage.setItem('recent' + this.currentSyllabaryName() + 'History', JSON.stringify(newHistory));

        Telemetry.send(new TelemetryData(
            (localStorage.getItem("knownHiraganaCharacters") || "").split('').length,
            (localStorage.getItem("knownKatakanaCharacters") || "").split('').length,
            history.length,
            this.state.sessionIdentifier
        ));

        this.setState(newState);
    }

    private currentSyllabaryName() {
        return this.state.currentSyllabary == Syllabary.katakana ? 'Katakana' : 'Hiragana';
    }

    private static getNextWord(
        newHistory: HistoryEvent[],
        knownWords: WordEntry[],
        knownCharacters: string) {
        return WordOracle.nextWord(newHistory,
            knownWords,
            knownCharacters,
            MIN_WORD_OPTIONS,
            RECENTS_TO_SKIP
        );
    }

    private getNextCharacter(knownCharacters: string): string | undefined {
        return this.currentCharacterOrder[knownCharacters.length];
    }
}

const StyledContent = styled.div`
  margin-top: 1em;
  padding-left: 2em;
  padding-right: 2em; 
`;

const theme = {
    japaneseFont: '游明朝, を指定, serif',
    kanaDisplaySize: '3em',
    highlightColor: '#F4A7B9',
    lightColor: '#ccc',
    transparentBackground: 'rgba(255, 255, 255, 0.9)',
};

export default App;



// WEBPACK FOOTER //
// ./src/components/App.tsx